import React from 'react';
import { Table, Button, Form } from 'react-bootstrap';
import BeaconLocation from './beacon-location';

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

const INTERVAL_OPTIONS = [
    { value: 0, label: 'No min interval' },
    { value: 1, label: '1 min' },
    { value: 2, label: '2 min' },
    { value: 5, label: '5 min' },
    { value: 10, label: '10 min' },
    { value: 15, label: '15 min' },
];

const BeaconTable = ({
    headers,
    data,
    title,
    locations = [],
    onExport,
    showBeaconIds = true,
    onToggleShowBeaconIds,
    selectedForMap,
    onToggleSelect,
    onSelectAll,
    onUnselectAll,
    mappableBeaconKeys,
    minIntervalMinutes = 0,
    onMinIntervalChange,
}) => {
    const rows = Array.isArray(data) ? data : [];
    const hasMapCheckbox = selectedForMap != null && typeof onToggleSelect === 'function';
    const filteredHeaders = showBeaconIds ? headers : headers.filter((h) => h !== 'major' && h !== 'minor');
    const dataHeaders = [...filteredHeaders, 'areas'];
    const displayHeaders = hasMapCheckbox ? ['Map', ...dataHeaders] : dataHeaders;
    const tableStyle = { fontSize: 13, marginBottom: 0, width: '100%', tableLayout: 'fixed' };
    const colWidth = `${100 / displayHeaders.length}%`;
    const isEmpty = rows.length === 0;

    if (isEmpty) {
        return (
            <div style={{ marginTop: 16 }}>
                {title && <p style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: '0 0 12px 0' }}>{title}</p>}
                <div
                    style={{
                        padding: '32px 24px',
                        textAlign: 'center',
                        backgroundColor: '#f9fafb',
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                    }}
                >
                    <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
                        No beacon events found for the selected date range.
                    </p>
                    <p style={{ margin: '8px 0 0', fontSize: 13, color: '#9ca3af' }}>
                        Beacon data requires the employee to have passed through areas with beacon coverage.
                    </p>
                </div>
            </div>
        );
    }

    const mappableRows = mappableBeaconKeys?.size
        ? rows.filter((row) => mappableBeaconKeys.has(row._rowKey))
        : rows;
    const allSelected =
        hasMapCheckbox &&
        selectedForMap &&
        mappableRows.length > 0 &&
        mappableRows.every((row) => selectedForMap.has(row._rowKey));

    return (
        <div style={{ marginTop: 16 }}>
            {(title || onMinIntervalChange || (hasMapCheckbox && (onSelectAll || onUnselectAll))) && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                    {title && <p style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: 0 }}>{title}</p>}
                    <div style={{ display: 'flex', gap: 8 }}>
                        {onMinIntervalChange && (
                            <Form.Select
                                size="sm"
                                style={{ width: 'auto', fontSize: 13 }}
                                value={minIntervalMinutes}
                                onChange={(e) => onMinIntervalChange(Number(e.target.value))}
                                aria-label="Min interval between map points"
                            >
                                {INTERVAL_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                        {hasMapCheckbox && (onSelectAll || onUnselectAll) && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={allSelected ? onUnselectAll : onSelectAll}
                            >
                                {allSelected ? 'Unselect all' : 'Select all'}
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {/* Fixed header table */}
            <Table striped bordered hover size="sm" style={tableStyle}>
                <colgroup>
                    {displayHeaders.map((header) => <col key={header} style={{ width: colWidth }} />)}
                </colgroup>
                <thead>
                    <tr>
                        {displayHeaders.map((header) => (
                            <th key={header}>{capitalize(header)}</th>
                        ))}
                    </tr>
                </thead>
            </Table>
            {/* Scrollable body */}
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderTop: 'none' }}>
                <Table striped bordered hover size="sm" style={tableStyle}>
                    <colgroup>
                        {displayHeaders.map((header) => <col key={header} style={{ width: colWidth }} />)}
                    </colgroup>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr key={row._rowKey ?? i}>
                                {hasMapCheckbox && (
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedForMap.has(row._rowKey)}
                                            onChange={() => onToggleSelect(row._rowKey)}
                                            disabled={mappableBeaconKeys?.size && !mappableBeaconKeys.has(row._rowKey)}
                                            aria-label={
                                                mappableBeaconKeys?.size && !mappableBeaconKeys.has(row._rowKey)
                                                    ? 'Beacon not mapped to a location'
                                                    : 'Show on map'
                                            }
                                            title={
                                                mappableBeaconKeys?.size && !mappableBeaconKeys.has(row._rowKey)
                                                    ? 'Beacon not mapped to a known airport location'
                                                    : undefined
                                            }
                                        />
                                    </td>
                                )}
                                {dataHeaders.map((header) => (
                                    <td key={header}>
                                        {header === 'areas' ? (
                                            <BeaconLocation beacon={row} locations={locations} />
                                        ) : (
                                            row[header]
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
            {(onExport || onToggleShowBeaconIds) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                    {onToggleShowBeaconIds && (
                        <Button
                            variant={showBeaconIds ? 'outline-secondary' : 'secondary'}
                            size="sm"
                            onClick={onToggleShowBeaconIds}
                        >
                            Major / Minor
                        </Button>
                    )}
                    {onExport && (
                        <Button variant="outline-secondary" size="sm" onClick={onExport}>
                            Export to CSV
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default BeaconTable;
